/**
 * Client for the generic attachments API (`/attachments`), keyed by
 * entityType + entityId. The backend is a single-call binary upload (raw
 * body, metadata in the query string) with a MIME allow-list and a 25 MB
 * cap — see apps/api/src/modules/attachments/.
 *
 * Downloads are permission-gated (bearer token), so images cannot be shown
 * with a bare <img src=…> — fetch them as blobs via `fetchAttachmentObjectUrl`
 * and render the resulting object URL.
 */
import { api } from './api.js';

/** Mirror of the server cap (attachments.service.ts MAX_BYTES). */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** Mirror of the server allow-list (attachments.service.ts ALLOWED_MIME). */
export const ATTACHMENT_ALLOWED_MIME: ReadonlySet<string> = new Set<string>([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

export interface Attachment {
  id: string;
  entityType: string;
  entityId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  uploadedByUserId: string | null;
  uploadedAt: string;
}

export function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

export async function listAttachments(entityType: string, entityId: string): Promise<Attachment[]> {
  const r = await api().get<{ items: Attachment[] }>('/attachments', {
    params: { entityType, entityId },
  });
  return r.data.items;
}

/**
 * Upload one file's raw bytes. Metadata travels in the query string; the body
 * is the ArrayBuffer (transformRequest passthrough stops axios JSON-encoding
 * it). Mirrors features/platform/LogoUpload.tsx.
 */
export async function uploadAttachment(
  entityType: string,
  entityId: string,
  file: File,
): Promise<Attachment> {
  const body = await file.arrayBuffer();
  const r = await api().post<Attachment>('/attachments', body, {
    params: { entityType, entityId, filename: file.name, mimeType: file.type },
    headers: { 'Content-Type': 'application/octet-stream' },
    transformRequest: [(d) => d],
  });
  return r.data;
}

export async function deleteAttachment(id: string): Promise<void> {
  await api().delete(`/attachments/${id}`);
}

/**
 * Fetch an attachment's bytes through the authed client and wrap them in an
 * object URL for <img>/download. The caller owns the URL — revoke it with
 * `URL.revokeObjectURL` when done to avoid leaking blobs.
 */
export async function fetchAttachmentObjectUrl(id: string): Promise<string> {
  const r = await api().get(`/attachments/${id}/download`, { responseType: 'blob' });
  return URL.createObjectURL(r.data as Blob);
}
